/**
 * Punkt 124 (Antoine, 26.09.2026) — die EINE Seite „Auswertung“: frühere Auswertung und Business
 * Health zusammengeführt. Freigegebenes Mockup `maestro-uebergabe/mockup-analyse-zusammengefuehrt.html`.
 *
 * Diese Seite rechnet nichts: jede Zahl kommt fertig aus `GET /api/auswertung`
 * (`lib/kennzahlen.ts` → `leiteAuswertungAb` in `@maestro/auswertung`). Hier passiert nur
 * Darstellung — Kacheln, ruhige SVG-Diagramme ohne Bibliothek, ein KI-Kasten, die Definitionen.
 * Alle Abschnitte nutzen DENSELBEN Zeitraum (Entscheidung 4); er steht in der Adresszeile, damit
 * ein Link genau diese Sicht wieder öffnet.
 */
import { useMemo, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  berlinTag,
  euro,
  prozent,
  STANDARD_TAGE,
  tagPlus,
  type AngebotsQuote,
  type Auswertung,
  type Buchungsstrecke,
  type Intervall,
  type Kennzahlen,
  type VideoQuote,
  type Zeitraum,
} from "@maestro/auswertung";
import { useApi } from "../hooks/useApi";
import { LeadAnalyseKarte } from "../ui/LeadAnalyseKarte";
import { Card, ErrorNote, Icon, Input, LoadingRows, PageHeader, DateInput, datumIsoZuDe } from "../ui/components";

/* ────────────────────────────── Farben der Diagramme (Mockup, hell/dunkel) ─────────────────── */

const S1 = "fill-[#c2410c] dark:fill-[#e0703f]";
const S3 = "fill-[#1f8a55] dark:fill-[#3aa672]";
const BG_S1 = "bg-[#c2410c] dark:bg-[#e0703f]";
const BG_S2 = "bg-[#2f6bc0] dark:bg-[#5b8fe0]";
const BG_S3 = "bg-[#1f8a55] dark:bg-[#3aa672]";
const BG_GHOST = "bg-[#cfc5b3] dark:bg-[#5a5042]";
const BG_WARN = "bg-[#d9a441] dark:bg-white/[0.08]";
// Achsentext in SVG-Einheiten: am Telefon wird das Diagramm auf ~45 % verkleinert, darum größer.
const ACHSE = "fill-[#6f675a] dark:fill-[#9a9080] text-[22px] md:text-[11px]";
const GITTER = "stroke-[#efe8da] dark:stroke-white/[0.07]";
const TEXT = "text-on-background dark:text-inverse-on-surface";
const LEISE = "text-secondary dark:text-secondary-fixed-dim";

const PILL = {
  ok: "bg-[#F0EAE0] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]",
  neu: "bg-[#F0EAE0] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]",
  warn: "bg-[#F0EAE0] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]",
  dang: "bg-[#F8E7E3] text-[#B23B32] dark:bg-[#B23B32]/22 dark:text-[#e39b92]",
} as const;

function Pille({ ton, icon, children }: { ton: keyof typeof PILL; icon?: string; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold ${PILL[ton]}`}>
      {icon && <Icon name={icon} className="text-[14px]" />}
      {children}
    </span>
  );
}

/* ────────────────────────────── Zahlen in Worten ───────────────────────────────────────────── */

const ganzeEuro = (cents: number) => euro(Math.round(cents / 100) * 100);
const de = (n: number) => n.toLocaleString("de-DE");
const prozent1 = (anteil: number) => `${(Math.round(anteil * 1000) / 10).toLocaleString("de-DE")} %`;

function dauer(minuten: number | null | undefined): string {
  if (minuten == null) return "—";
  return minuten < 120 ? `${minuten} Min.` : `${Math.round(minuten / 60)} Std.`;
}

/** Veränderung gegenüber dem Vergleich — die Farbe sagt „besser“, grau heißt „nicht besser“. */
function veraenderung(jetzt: number | null, vorher: number | null, art: "prozent" | "punkte" | "stunden", kleinerIstBesser = false) {
  if (jetzt === null || vorher === null) return null;
  let diff: number;
  let betrag: string;
  if (art === "prozent") {
    if (vorher === 0) return null;
    diff = (jetzt - vorher) / vorher;
    betrag = `${Math.abs(Math.round(diff * 100))} %`;
  } else if (art === "punkte") {
    diff = (jetzt - vorher) * 100;
    betrag = `${(Math.round(Math.abs(diff) * 10) / 10).toLocaleString("de-DE")} Pkt.`;
  } else {
    diff = (jetzt - vorher) / 60;
    betrag = `${Math.abs(Math.round(diff))} Std.`;
  }
  const zeichen = diff > 0 ? "+" : diff < 0 ? "−" : "±";
  const besser = kleinerIstBesser ? diff < 0 : diff > 0;
  return { text: `${zeichen}${betrag}`, ton: besser ? ("ok" as const) : ("neu" as const) };
}

/* ────────────────────────────── Zeitraum ───────────────────────────────────────────────────── */

type Schnellwahl = "monat" | "30" | "90" | "365";
const SCHNELLWAHL: readonly { wert: Schnellwahl; text: string }[] = [
  { wert: "monat", text: "Monat" },
  { wert: "30", text: "30 T" },
  { wert: "90", text: "90 T" },
  { wert: "365", text: "12 M" },
];

function zeitraumAus(wahl: Schnellwahl, heute: string): Zeitraum {
  if (wahl === "monat") return { von: `${heute.slice(0, 7)}-01`, bis: heute };
  return { von: tagPlus(heute, -(Number(wahl) - 1)), bis: heute };
}

function Umschalter<T extends string>({
  werte,
  aktiv,
  onWahl,
  label,
}: {
  werte: readonly { wert: T; text: string }[];
  aktiv: T | null;
  onWahl: (w: T) => void;
  label: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="inline-flex flex-wrap gap-0.5 rounded-full border border-outline-variant/60 bg-surface-container-lowest p-[3px] dark:border-outline/40 dark:bg-night-card"
    >
      {werte.map((w) => (
        <button
          key={w.wert}
          type="button"
          aria-pressed={aktiv === w.wert}
          onClick={() => onWahl(w.wert)}
          className={`rounded-full px-3 py-1 text-[12px] font-semibold transition-colors ${
            aktiv === w.wert
              ? "bg-primary/10 text-primary dark:bg-primary/20 dark:text-[#f0b49b]"
              : `${LEISE} hover:text-on-background dark:hover:text-inverse-on-surface`
          }`}
        >
          {w.text}
        </button>
      ))}
    </div>
  );
}

/* ────────────────────────────── Bausteine ──────────────────────────────────────────────────── */

function Sparkline({ werte }: { werte: (number | null)[] }) {
  const w = werte.map((x) => x ?? 0);
  if (w.length < 2) return <div className="h-[30px]" />;
  const max = Math.max(...w);
  const min = Math.min(...w);
  const punkte = w
    .map((v, i) => `${((i * 120) / (w.length - 1)).toFixed(1)},${(27 - ((v - min) / (max - min || 1)) * 22).toFixed(1)}`)
    .join(" ");
  return (
    <svg viewBox="0 0 120 30" preserveAspectRatio="none" aria-hidden="true" className="mt-1 block h-[30px] w-full">
      <polyline
        points={punkte}
        fill="none"
        strokeWidth={2}
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
        strokeLinecap="round"
        className="stroke-[#c2410c] dark:stroke-[#e0703f]"
      />
    </svg>
  );
}

function Kachel({
  titel,
  erklaerung,
  wert,
  delta,
  kontext,
  verlauf,
  testId,
}: {
  titel: string;
  erklaerung: string;
  wert: string;
  delta: ReturnType<typeof veraenderung>;
  kontext: string;
  verlauf: (number | null)[];
  testId: string;
}) {
  return (
    <Card className="flex min-w-0 flex-col gap-1 px-3.5 pb-2.5 pt-3.5" data-testid={testId}>
      <div className="flex justify-between gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.07em] text-[#9A9080] dark:text-secondary-fixed-dim">
        <span>{titel}</span>
        <span title={erklaerung} className="cursor-help">
          <Icon name="info" className="text-[15px]" />
        </span>
      </div>
      <div className={`text-[25px] font-extrabold leading-tight tracking-[-0.02em] ${TEXT}`} data-testid={`${testId}-wert`}>
        {wert}
      </div>
      <div className={`flex flex-wrap items-center gap-1.5 text-[11.5px] ${LEISE}`}>
        {delta && <Pille ton={delta.ton}>{delta.text}</Pille>}
        <span>{kontext}</span>
      </div>
      <Sparkline werte={verlauf} />
    </Card>
  );
}

function Abschnitt({
  icon,
  titel,
  notiz,
  rechts,
  children,
  testId,
}: {
  icon: string;
  titel: string;
  notiz?: ReactNode;
  rechts?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <Card className="p-[18px]" data-testid={testId}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className={`flex items-center gap-2 text-[16px] font-bold ${TEXT}`}>
          <Icon name={icon} className="text-[18px] text-primary dark:text-[#f0b49b]" />
          {titel}
        </h2>
        {rechts}
        {notiz && <span className={`text-[12px] ${LEISE}`}>{notiz}</span>}
      </div>
      {children}
    </Card>
  );
}

function Legende({ eintraege }: { eintraege: { farbe: string; text: string; gestrichelt?: boolean }[] }) {
  return (
    <div className="mb-2 flex flex-wrap gap-3.5 text-[12px] text-[#57503f] dark:text-[#b8ae9e]">
      {eintraege.map((e) => (
        <span key={e.text} className="inline-flex items-center gap-1.5">
          {e.gestrichelt ? (
            <i className="inline-block w-3.5 border-t-2 border-dashed border-[#cfc5b3] dark:border-[#5a5042]" />
          ) : (
            <i className={`inline-block h-2.5 w-2.5 rounded-[3px] ${e.farbe}`} />
          )}
          {e.text}
        </span>
      ))}
    </div>
  );
}

/** Balken mit rundem oberen Ende, flach an der Grundlinie (wie im Mockup). */
function balkenPfad(x: number, y: number, w: number, h: number, r = 4): string {
  if (h <= 0) return "";
  const rr = Math.min(r, h, w / 2);
  return `M${x},${y + h}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${y + h}Z`;
}

/** Runde Achsenobergrenze: 1/2/5/10 × 10^n. */
function achsenMax(max: number): number {
  if (max <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(max));
  return [1, 2, 5, 10].map((f) => f * p).find((s) => s >= max) ?? max;
}

function AnfragenDiagramm({ a, vergleichWort }: { a: Auswertung; vergleichWort: string }) {
  const W = 720;
  const H = 190;
  const L = 28;
  const B = 24;
  const T = 8;
  const werte = a.verlauf.map((p) => p.kennzahlen.anfragen);
  const vorher = a.verlauf.map((p) => p.vorherAnfragen);
  const max = achsenMax(Math.max(1, ...werte, ...vorher));
  const n = werte.length;
  const schritt = (W - L) / Math.max(n, 1);
  const bw = schritt * 0.56;
  const y = (v: number) => T + (H - T - B) * (1 - v / max);
  const beschriften = Math.max(1, Math.ceil(n / 8));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Anfragen je Woche, verglichen mit ${vergleichWort}`} className="block w-full overflow-visible" data-testid="diagramm-anfragen">
      {[0, max / 2, max].map((g) => (
        <g key={g}>
          <line x1={L} x2={W} y1={y(g)} y2={y(g)} className={GITTER} />
          <text x={L - 8} y={y(g) + 4} textAnchor="end" className={ACHSE}>
            {de(Math.round(g))}
          </text>
        </g>
      ))}
      {werte.map((v, i) => {
        const x = L + i * schritt + (schritt - bw) / 2;
        return (
          <g key={i}>
            <path d={balkenPfad(x, y(v), bw, y(0) - y(v))} className={S1} />
            {(i % beschriften === 0 || i === n - 1) && (
              <text x={x + bw / 2} y={H - 6} textAnchor="middle" className={ACHSE}>
                {a.verlauf[i]!.titel}
              </text>
            )}
            <rect x={L + i * schritt} y={T} width={schritt} height={H - T - B} rx={6} className="fill-transparent hover:fill-primary/10">
              <title>{`ab ${a.verlauf[i]!.titel}: ${v} Anfragen · ${vergleichWort}: ${vorher[i]}`}</title>
            </rect>
          </g>
        );
      })}
      {n > 1 && (
        <polyline
          points={vorher.map((v, i) => `${L + i * schritt + schritt / 2},${y(v)}`).join(" ")}
          fill="none"
          strokeWidth={2}
          strokeDasharray="5 4"
          className="pointer-events-none stroke-[#cfc5b3] dark:stroke-[#5a5042]"
        />
      )}
    </svg>
  );
}

function UmsatzDiagramm({ a }: { a: Auswertung }) {
  const W = 520;
  const H = 210;
  const L = 48;
  const B = 24;
  const T = 10;
  const p = a.umsatzVerlauf;
  const max = achsenMax(Math.max(100, ...p.map((x) => Math.max(x.zugesagtCents, x.eingegangenCents))));
  const schritt = (W - L) / Math.max(p.length, 1);
  const bw = Math.min(schritt * 0.3, 28);
  const y = (v: number) => T + (H - T - B) * (1 - v / max);
  const kurz = (c: number) => (c >= 100_000 ? `${de(Math.round(c / 100_000))} T€` : ganzeEuro(c));
  const beschriften = Math.max(1, Math.ceil(p.length / 8));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Zugesagter und eingegangener Umsatz" className="block w-full overflow-visible" data-testid="diagramm-umsatz">
      {[0, max / 2, max].map((g) => (
        <g key={g}>
          <line x1={L} x2={W} y1={y(g)} y2={y(g)} className={GITTER} />
          <text x={L - 8} y={y(g) + 4} textAnchor="end" className={ACHSE}>
            {g === 0 ? "0" : kurz(g)}
          </text>
        </g>
      ))}
      {p.map((m, i) => {
        const mitte = L + i * schritt + schritt / 2;
        return (
          <g key={i}>
            <path d={balkenPfad(mitte - bw - 1, y(m.zugesagtCents), bw, y(0) - y(m.zugesagtCents))} className={S1} />
            <path d={balkenPfad(mitte + 1, y(m.eingegangenCents), bw, y(0) - y(m.eingegangenCents))} className={S3} />
            {(i % beschriften === 0 || i === p.length - 1) && (
              <text x={mitte} y={H - 6} textAnchor="middle" className={ACHSE}>
                {m.titel}
              </text>
            )}
            <rect x={L + i * schritt} y={T} width={schritt} height={H - T - B} rx={6} className="fill-transparent hover:fill-primary/10">
              <title>{`${m.titel}: zugesagt ${ganzeEuro(m.zugesagtCents)} · auf dem Konto ${ganzeEuro(m.eingegangenCents)}`}</title>
            </rect>
          </g>
        );
      })}
    </svg>
  );
}

function Stufe({ titel, wert, zusatz, anteil, farbe }: { titel: ReactNode; wert: ReactNode; zusatz?: string; anteil: number; farbe: string }) {
  return (
    <div className="my-2.5">
      <div className={`mb-1 flex justify-between gap-2 text-[12.5px] ${TEXT}`}>
        <span>{titel}</span>
        <b className="whitespace-nowrap tabular-nums">
          {wert}
          {zusatz && <span className={`font-normal ${LEISE}`}> · {zusatz}</span>}
        </b>
      </div>
      <div className="h-3.5 overflow-hidden rounded bg-[#f6f0e4] dark:bg-white/[0.05]">
        <span
          className={`block h-full rounded-r ${farbe} motion-safe:transition-[width]`}
          style={{ width: `${Math.max(0, Math.min(1, anteil)) * 100}%` }}
        />
      </div>
    </div>
  );
}

function bereich(q: Intervall | null): string {
  return q ? `${Math.round(q.unten * 100)}–${Math.round(q.oben * 100)} %` : "—";
}

/* ────────────────────────────── Buchungsstrecke (D5) ───────────────────────────────────────── */

/**
 * Buchungsstrecke D5 (docs/LOOP-BUCHUNGSSTRECKE.md, Mockup Station 8): Anfrage → Angebot →
 * angesehen → gebucht je Monat/Kanal, Verlustgründe als Balken, vorher/nachher je Phase. Alle
 * Zahlen kommen fertig aus `leiteBuchungsstreckeAb` — hier wird nur gezeichnet. Keine Rangliste
 * einzelner Mitarbeiter (Konzept § 5).
 */
function angesehenText(angesehen: number, gemessen: number): string {
  return gemessen > 0 ? `${de(angesehen)} von ${de(gemessen)}` : "—";
}

function QuoteZeile({ titel, q }: { titel: string; q: AngebotsQuote }) {
  return (
    <div className={`flex flex-wrap items-baseline justify-between gap-2 border-b border-[#f6f0e4] py-2 text-[13px] last:border-b-0 dark:border-white/[0.06] ${TEXT}`}>
      <span>
        {titel}{" "}
        <span className={`text-[12px] ${LEISE}`}>
          {datumIsoZuDe(q.zeitraum.von)} – {datumIsoZuDe(q.zeitraum.bis)}
        </span>
      </span>
      {q.zuWenig || !q.quote ? (
        <span className={`text-[12px] ${LEISE}`}>
          noch zu wenig Daten ({q.basis} Angebote{q.nochInFrist ? `, ${q.nochInFrist} in Frist` : ""})
        </span>
      ) : (
        <b className="tabular-nums">
          {prozent1(q.quote.anteil)}{" "}
          <span className={`font-normal ${LEISE}`}>
            · Bereich {bereich(q.quote)} · {q.gebucht} von {q.basis}
          </span>
        </b>
      )}
    </div>
  );
}

/**
 * B4 (sql/603): Begrüßungsvideo — Abspielquote unter angesehenen Angeboten und Buchungsquote
 * mit/ohne Abspielen. Erscheint nur, wenn im Zeitraum mindestens einmal abgespielt wurde.
 */
function VideoZeile({ q }: { q: VideoQuote }) {
  const ohne = q.angesehen - q.abgespielt;
  return (
    <p className={`mt-2 text-[12px] ${LEISE}`} data-testid="strecke-video">
      <b className={TEXT}>Begrüßungsvideo:</b> auf {q.abgespielt} von {q.angesehen} angesehenen Angeboten abgespielt
      {q.abspielquote ? ` (${prozent(q.abspielquote.anteil)})` : ""}. Gebucht mit Abspielen {q.gebuchtMit} von {q.abgespielt}
      {q.buchungMit ? ` (${prozent(q.buchungMit.anteil)}, Bereich ${bereich(q.buchungMit)})` : ""}, ohne Abspielen {q.gebuchtOhne} von {ohne}
      {q.buchungOhne ? ` (${prozent(q.buchungOhne.anteil)}, Bereich ${bereich(q.buchungOhne)})` : ""}. Ein Zusammenhang, kein Beweis: wer ohnehin buchen will, schaut eher.
    </p>
  );
}

function BuchungsstreckeAbschnitt({ s }: { s: Buchungsstrecke }) {
  const [sicht, setSicht] = useState<"monat" | "kanal">("monat");
  const g = s.gesamt;
  const h = s.hauptkennzahl;
  const zeilen = sicht === "monat" ? s.jeMonat : s.jeKanal;
  const maxZeile = Math.max(1, ...zeilen.map((z) => z.stufen.anfragen));
  const v = s.verlust;
  const verlustBalken = [
    ...v.gruende.map((r) => ({ schluessel: r.schluessel, titel: r.titel, anzahl: r.anzahl, farbe: BG_S2, gast: r.quelle === "gast_antwort" })),
    { schluessel: "keine_rueckmeldung", titel: "Keine Rückmeldung", anzahl: v.keineRueckmeldung, farbe: BG_GHOST, gast: false },
    { schluessel: "ohne_grund", titel: "Abgelehnt ohne Grund", anzahl: v.ohneGrund, farbe: BG_GHOST, gast: false },
  ].filter((r) => r.anzahl > 0);
  const maxVerlust = Math.max(1, ...verlustBalken.map((r) => r.anzahl));

  return (
    <Abschnitt icon="route" titel="Buchungsstrecke" notiz="Anfrage → Angebot → angesehen → gebucht · Testvorgänge ausgeschlossen" testId="abschnitt-buchungsstrecke">
      <div className="grid grid-cols-1 gap-[22px] lg:grid-cols-2">
        <div>
          <Stufe titel="Anfragen" wert={de(g.anfragen)} anteil={g.anfragen ? 1 : 0} farbe={BG_S1} />
          <Stufe
            titel="Angebot verschickt"
            wert={de(g.angebote)}
            zusatz={g.anfragen ? prozent(g.angebote / g.anfragen) : undefined}
            anteil={g.anfragen ? g.angebote / g.anfragen : 0}
            farbe={BG_S1}
          />
          <Stufe
            titel="Vom Gast angesehen"
            wert={angesehenText(g.angesehen, g.angeboteGemessen)}
            zusatz={g.angeboteGemessen ? prozent(g.angesehen / g.angeboteGemessen) : undefined}
            anteil={g.anfragen && g.angeboteGemessen ? (g.angesehen / g.angeboteGemessen) * (g.angebote / g.anfragen) : 0}
            farbe={BG_S2}
          />
          <Stufe
            titel="Gebucht"
            wert={de(g.gebucht)}
            zusatz={g.anfragen ? prozent(g.gebucht / g.anfragen) : undefined}
            anteil={g.anfragen ? g.gebucht / g.anfragen : 0}
            farbe={BG_S3}
          />
          <p className={`mt-2 text-[12px] ${LEISE}`} data-testid="strecke-hauptkennzahl">
            <b className={TEXT}>Hauptzahl:</b>{" "}
            {h.zuWenig || !h.quote
              ? `Anteil der verschickten Angebote, die binnen 21 Tagen gebucht werden — noch zu wenig Daten (${h.basis} Angebote mit abgelaufener Frist, ab 20 wird gerechnet).`
              : `${prozent1(h.quote.anteil)} der verschickten Angebote wurden binnen 21 Tagen gebucht (${h.gebucht} von ${h.basis}, belastbarer Bereich ${bereich(h.quote)}).`}
            {h.nochInFrist > 0 && ` ${h.nochInFrist} ${h.nochInFrist === 1 ? "Angebot ist" : "Angebote sind"} noch in der Frist und zählen erst danach.`}
          </p>
          {s.video && <VideoZeile q={s.video} />}
        </div>

        <div data-testid="strecke-verlust">
          <h3 className={`mb-1 text-[14px] font-bold ${TEXT}`}>Warum nicht gebucht?</h3>
          {verlustBalken.length === 0 ? (
            <p className={`text-[12px] ${LEISE}`}>Im Zeitraum ist kein Verlust notiert.</p>
          ) : (
            verlustBalken.map((r) => (
              <div key={r.schluessel} data-testid="verlust-zeile" title={`${r.titel}: ${r.anzahl}`}>
                <Stufe
                  titel={
                    <>
                      {r.titel}
                      {r.gast && (
                        <>
                          {" "}
                          <Pille ton="neu">Antwort des Gastes</Pille>
                        </>
                      )}
                    </>
                  }
                  wert={de(r.anzahl)}
                  anteil={r.anzahl / maxVerlust}
                  farbe={r.farbe}
                />
              </div>
            ))
          )}
          <p className={`mt-1 text-[12px] ${LEISE}`}>
            Ein Grund je Vorgang, nur gezählt: zuerst die Antwort auf die Nachfrage-Mail, dann der notierte Grund. „Keine Rückmeldung“ steht getrennt.
          </p>
        </div>
      </div>

      <div className="mb-1 mt-[22px] flex flex-wrap items-center justify-between gap-2">
        <h3 className={`text-[14px] font-bold ${TEXT}`}>Je {sicht === "monat" ? "Monat" : "Kanal"}</h3>
        <Umschalter
          label="Buchungsstrecke aufteilen nach"
          werte={[{ wert: "monat", text: "je Monat" }, { wert: "kanal", text: "je Kanal" }] as const}
          aktiv={sicht}
          onWahl={setSicht}
        />
      </div>
      <div data-testid="strecke-tabelle">
        <div className="hidden grid-cols-[minmax(0,1.4fr)_repeat(4,minmax(0,.7fr))_minmax(0,1.3fr)] items-center gap-2.5 border-b border-[#f2ecdf] px-1 py-2.5 text-[10.5px] font-bold uppercase tracking-[0.07em] text-[#9A9080] dark:border-white/[0.06] dark:text-secondary-fixed-dim md:grid">
          <span>{sicht === "monat" ? "Monat" : "Kanal"}</span>
          <span className="text-right">Anfragen</span>
          <span className="text-right">Angebot</span>
          <span className="text-right">Angesehen</span>
          <span className="text-right">Gebucht</span>
          <span aria-hidden="true" />
        </div>
        {zeilen.map((z) => (
          <div
            key={z.titel}
            data-testid="strecke-zeile"
            title={`${z.titel}: ${z.stufen.anfragen} Anfragen · ${z.stufen.angebote} Angebote · ${z.stufen.angesehen} angesehen · ${z.stufen.gebucht} gebucht`}
            className={`grid grid-cols-4 items-center gap-x-2.5 gap-y-1 border-b border-[#f6f0e4] px-1 py-2.5 text-[13px] dark:border-white/[0.06] md:grid-cols-[minmax(0,1.4fr)_repeat(4,minmax(0,.7fr))_minmax(0,1.3fr)] ${TEXT}`}
          >
            <b className="col-span-4 font-semibold md:col-span-1">{z.titel}</b>
            {(
              [
                ["Anfragen", de(z.stufen.anfragen)],
                ["Angebot", de(z.stufen.angebote)],
                ["Angesehen", angesehenText(z.stufen.angesehen, z.stufen.angeboteGemessen)],
                ["Gebucht", `${z.stufen.gebucht}${z.stufen.anfragen ? ` (${Math.round((z.stufen.gebucht / z.stufen.anfragen) * 100)} %)` : ""}`],
              ] as const
            ).map(([lb, wert]) => (
              <div key={lb} className="tabular-nums md:text-right">
                <span className={`block text-[10px] uppercase tracking-[0.06em] md:hidden ${LEISE}`}>{lb}</span>
                {wert}
              </div>
            ))}
            <div className="col-span-4 flex flex-col gap-[3px] md:col-span-1" aria-hidden="true">
              {[
                [z.stufen.anfragen, BG_S1],
                [z.stufen.angebote, BG_S1],
                [z.stufen.angesehen, BG_S2],
                [z.stufen.gebucht, BG_S3],
              ].map(([n, farbe], i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-r ${farbe} ${i === 1 ? "opacity-70" : ""}`}
                  style={{ width: `${(n as number) > 0 ? Math.max(((n as number) / maxZeile) * 100, 1) : 0}%` }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <h3 className={`mb-1 mt-[22px] text-[14px] font-bold ${TEXT}`}>Wirkung je Phase</h3>
      <p className={`mb-2 text-[12px] ${LEISE}`}>
        Hauptzahl in den 90 Tagen vor dem Stichtag gegen die Zeit danach. Unter 20 Angeboten je Seite steht keine Zahl.
      </p>
      <div className="flex flex-col gap-3" data-testid="strecke-phasen">
        {s.phasen.length === 0 && <p className={`text-[12px] ${LEISE}`}>Noch keine Phase live.</p>}
        {s.phasen.map((p) => (
          <div key={p.id} data-testid={`phase-${p.id}`} className="rounded-lg border border-[#f2ecdf] px-3 py-2 dark:border-white/[0.06]">
            <div className={`flex flex-wrap items-baseline justify-between gap-2 text-[13px] ${TEXT}`}>
              <b>{p.titel}</b>
              <span className={`text-[12px] ${LEISE}`}>
                live seit {datumIsoZuDe(p.stichtag)} · {p.punkte.join(", ")}
              </span>
            </div>
            <QuoteZeile titel="Vorher" q={p.vorher} />
            <QuoteZeile titel="Nachher" q={p.nachher} />
            <p className={`mt-1 text-[12px] ${LEISE}`}>{p.satz}</p>
          </div>
        ))}
        {s.phasenOffen.length > 0 && <p className={`text-[12px] ${LEISE}`}>Noch nicht live: {s.phasenOffen.join(" · ")}.</p>}
      </div>
    </Abschnitt>
  );
}

/* ────────────────────────────── So wird gezählt (Mockup, verbindlich) ─────────────────────── */

const DEFINITIONEN: [string, string][] = [
  ["Anfrage", "Anfrage mit Eingang im Zeitraum, ohne Test- und interne Anfragen und ohne Probeläufe. Checkout-Bestellungen und Vormerkungen stehen getrennt."],
  ["Auftrag / gebucht", "Gewonnen (Angebot angenommen, Kostenübernahme unterschrieben, voll bezahlt netto oder als gewonnen markiert), abzüglich storniert. Stornos als eigene Zahl."],
  ["Buchungsquote", "Aufträge aus Anfragen ÷ Anfragen desselben Zeitraums, mit belastbarem Bereich (Wilson, 95 %)."],
  ["Umsatz zugesagt", "Angenommener Auftragsbetrag, gezählt am Annahmetag (umschaltbar auf den Veranstaltungstag), alle Wege (Anfrage, Checkout, direkt), ohne storniert."],
  ["Auf dem Konto", "Erfolgreiche Zahlungen netto nach Erstattung, gezählt am Zahltag."],
  ["Ø Auftragswert", "Umsatz zugesagt ÷ Zahl ALLER Aufträge derselben Menge (Zähler und Nenner gleich geschnitten)."],
  ["Formular-Aufrufe", "Wie oft ein Formular angezeigt wurde. Die Kennzahl heißt „Anfragen je 100 Aufrufe“, nicht Buchungsquote."],
  ["Antwortzeit", "Eingang bis zur ersten Nachricht ODER zum ersten Angebot aus MAESTRO, was früher war. Median plus Anteil binnen 24 Std."],
  ["Angesehen", "Die Angebotsseite wurde vom Gast mindestens einmal geöffnet; die Vorschau des Betriebs zählt nicht. Nur Angebote, deren Aufrufe gemessen werden."],
  ["Hauptzahl Buchungsstrecke", "Verschickte Angebote (nach Versandtag), die binnen 21 Tagen gebucht wurden, ÷ verschickte Angebote, deren 21 Tage vorbei sind. Mit belastbarem Bereich (Wilson, 95 %)."],
  ["Begrüßungsvideo", "Unter den vom Gast angesehenen Angeboten: wie oft das Begrüßungsvideo abgespielt wurde (einmal je Vorgang, nur solange das Angebot offen ist), und wie viele davon gebucht haben, verglichen mit denen ohne Abspielen."],
  ["Verlustgrund", "Ein Grund je nicht gebuchter Anfrage: Antwort auf die Nachfrage-Mail, sonst der notierte Grund beim Ablehnen. Verfall ohne Antwort als „Keine Rückmeldung“."],
  ["Vorperiode", "Gleich langer Zeitraum direkt davor, bei „Monat“ dieselben Tage des Vormonats. Mit „vs. Vorjahr“ derselbe Zeitraum ein Jahr früher, wegen der Saison."],
];

/* ────────────────────────────── Die Seite ──────────────────────────────────────────────────── */

export default function AuswertungPage() {
  const [such, setSuch] = useSearchParams();
  const heute = useMemo(() => berlinTag(new Date()), []);
  const frei = such.get("von") && such.get("bis");
  const wahl: Schnellwahl | null = frei ? null : ((such.get("zr") as Schnellwahl | null) ?? (String(STANDARD_TAGE) as Schnellwahl));
  const zeitraum: Zeitraum = wahl ? zeitraumAus(wahl, heute) : { von: such.get("von")!, bis: such.get("bis")! };
  const vergleich = such.get("vergleich") === "vorjahr" ? "vorjahr" : "vorperiode";
  const umsatzNach = such.get("umsatzNach") === "veranstaltung" ? "veranstaltung" : "annahme";

  const setze = (neu: Record<string, string | null>) => {
    const s = new URLSearchParams(such);
    for (const [k, v] of Object.entries(neu)) {
      if (v === null) s.delete(k);
      else s.set(k, v);
    }
    setSuch(s, { replace: true });
  };

  const { data: a, loading, error, reload } = useApi<Auswertung>(
    `/api/auswertung?von=${zeitraum.von}&bis=${zeitraum.bis}&vergleich=${vergleich}&umsatzNach=${umsatzNach}`,
  );

  const kopf = (
    <div className="flex flex-col items-start gap-2 sm:items-end">
      <div className="flex flex-wrap items-center gap-2">
        <Umschalter label="Zeitraum" werte={SCHNELLWAHL} aktiv={wahl} onWahl={(w) => setze({ zr: w, von: null, bis: null })} />
        <Umschalter
          label="Vergleich"
          werte={[{ wert: "vorjahr", text: "vs. Vorjahr" }] as const}
          aktiv={vergleich === "vorjahr" ? "vorjahr" : null}
          onWahl={() => setze({ vergleich: vergleich === "vorjahr" ? null : "vorjahr" })}
        />
      </div>
      <div className={`flex flex-wrap items-center gap-2 text-[12px] ${LEISE}`}>
        <span>Von</span>
        <DateInput
          aria-label="Von"
          className="!w-auto" inputClassName="!py-1 text-[12px]"
          value={zeitraum.von}
          max={zeitraum.bis}
          onValueChange={(v) => v && setze({ zr: null, von: v, bis: zeitraum.bis })}
        />
        <span>bis</span>
        <DateInput
          aria-label="Bis"
          className="!w-auto" inputClassName="!py-1 text-[12px]"
          value={zeitraum.bis}
          min={zeitraum.von}
          onValueChange={(v) => v && setze({ zr: null, von: zeitraum.von, bis: v })}
        />
      </div>
    </div>
  );

  if (!a) {
    return (
      <>
        <PageHeader title="Auswertung" actions={kopf} />
        {error ? <ErrorNote message={error} onRetry={reload} /> : <LoadingRows rows={6} />}
      </>
    );
  }

  const k: Kennzahlen = a.aktuell;
  const v: Kennzahlen = a.vorher;
  const reihe = (f: (x: Kennzahlen) => number | null) => a.verlauf.map((p) => f(p.kennzahlen));
  const maxEingang = Math.max(1, ...a.eingaenge.zeilen.map((z) => z.anzahl));
  const maxQuote = Math.max(0.01, ...a.antwortklassen.map((z) => z.quote?.anteil ?? 0));
  const vergleichWort = vergleich === "vorjahr" ? "Vorjahr" : "Vorperiode";
  const oz = a.offeneZahlungen;

  return (
    <>
      <PageHeader
        title="Auswertung"
        subtitle={
          <span data-testid="auswertung-zeitraum">
            {a.zeitraum.text} · {a.vergleich.text}
          </span>
        }
        actions={kopf}
      />
      {error && <ErrorNote message={error} onRetry={reload} />}

      <div className={`grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6 ${loading ? "opacity-60" : ""}`}>
        <Kachel
          testId="kz-anfragen"
          titel="Anfragen"
          erklaerung="Ohne Test- und interne Anfragen, Datum = Eingang"
          wert={de(k.anfragen)}
          delta={veraenderung(k.anfragen, v.anfragen, "prozent")}
          kontext={`${vergleichWort} ${de(v.anfragen)}`}
          verlauf={reihe((x) => x.anfragen)}
        />
        <Kachel
          testId="kz-quote"
          titel="Buchungsquote"
          erklaerung="Gewonnen minus storniert, geteilt durch Anfragen"
          wert={k.buchungsquote ? prozent1(k.buchungsquote.anteil) : "—"}
          delta={veraenderung(k.buchungsquote?.anteil ?? null, v.buchungsquote?.anteil ?? null, "punkte")}
          kontext={`${k.auftraege} von ${k.anfragen}${k.storniert ? ` · ${k.storniert} storniert` : ""}`}
          verlauf={reihe((x) => x.buchungsquote?.anteil ?? null)}
        />
        <Kachel
          testId="kz-zugesagt"
          titel="Zugesagt"
          erklaerung={umsatzNach === "annahme" ? "Angenommener Betrag am Annahmetag, alle Wege" : "Angenommener Betrag am Veranstaltungstag, alle Wege"}
          wert={ganzeEuro(k.zugesagtCents)}
          delta={veraenderung(k.zugesagtCents, v.zugesagtCents, "prozent")}
          kontext={`${vergleichWort} ${ganzeEuro(v.zugesagtCents)}`}
          verlauf={reihe((x) => x.zugesagtCents)}
        />
        <Kachel
          testId="kz-konto"
          titel="Auf dem Konto"
          erklaerung="Zahlungen netto nach Erstattung, am Zahltag"
          wert={ganzeEuro(k.eingegangenCents)}
          delta={veraenderung(k.eingegangenCents, v.eingegangenCents, "prozent")}
          kontext={`${oz.anzahl} ${oz.anzahl === 1 ? "Zahlung" : "Zahlungen"} offen`}
          verlauf={reihe((x) => x.eingegangenCents)}
        />
        <Kachel
          testId="kz-schnitt"
          titel="Ø Auftragswert"
          erklaerung="Zugesagt ÷ alle Aufträge (gleiche Menge)"
          wert={k.schnittCents === null ? "—" : ganzeEuro(k.schnittCents)}
          delta={veraenderung(k.schnittCents, v.schnittCents, "prozent")}
          kontext={`${k.auftraegeAlle} ${k.auftraegeAlle === 1 ? "Auftrag" : "Aufträge"}${k.checkout ? " inkl. Checkout" : ""}`}
          verlauf={reihe((x) => x.schnittCents)}
        />
        <Kachel
          testId="kz-antwort"
          titel="Antwortzeit"
          erklaerung="Bis zur ersten Nachricht oder zum ersten Angebot"
          wert={dauer(k.antwortzeit?.medianMinuten)}
          delta={veraenderung(k.antwortzeit?.medianMinuten ?? null, v.antwortzeit?.medianMinuten ?? null, "stunden", true)}
          kontext={k.antwortzeit ? `Median · ${prozent(k.antwortzeit.anteilBinnen24h)} binnen 24 Std.` : "noch nichts zu messen"}
          verlauf={reihe((x) => x.antwortzeit?.medianMinuten ?? null)}
        />
      </div>

      {/* ── Anfragen & Herkunft ───────────────────────────────────────────────────────────── */}
      <div className="mt-[22px]">
        <Abschnitt icon="move_to_inbox" titel="Anfragen & Herkunft" notiz="Test- und interne Anfragen ausgeschlossen · Datum = Eingang der Anfrage" testId="abschnitt-anfragen">
          <Legende eintraege={[{ farbe: BG_S1, text: "Anfragen je Woche" }, { farbe: "", text: vergleichWort, gestrichelt: true }]} />
          <AnfragenDiagramm a={a} vergleichWort={vergleichWort} />

          <h3 className={`mb-1 mt-[22px] text-[14px] font-bold ${TEXT}`}>Worüber kommen deine Anfragen?</h3>
          <p className={`mb-2 text-[12px] ${LEISE}`}>
            {a.eingaenge.satz} Vom Formular-Aufruf bis zur Buchung, je Eingang. „—“ heißt nicht gemessen, nicht null.
          </p>
          <div data-testid="eingaenge-karte">
            <div className="hidden grid-cols-[minmax(0,1.6fr)_repeat(4,minmax(0,.7fr))_minmax(0,1.3fr)] items-center gap-2.5 border-b border-[#f2ecdf] px-1 py-2.5 text-[10.5px] font-bold uppercase tracking-[0.07em] text-[#9A9080] dark:border-white/[0.06] dark:text-secondary-fixed-dim md:grid">
              <span>Eingang</span>
              <span className="text-right">Aufrufe</span>
              <span className="text-right">Anfragen</span>
              <span className="text-right">Angebot</span>
              <span className="text-right">Gebucht</span>
              <span>Anfrage → Angebot → Buchung</span>
            </div>
            {a.eingaenge.zeilen.map((z) => (
              <div
                key={z.titel}
                data-testid="eingang-zeile"
                title={`${z.titel}: ${z.anzahl} Anfragen · ${z.angebote} mit Angebot · ${z.gebucht} gebucht`}
                className={`grid grid-cols-4 items-center gap-x-2.5 gap-y-1 border-b border-[#f6f0e4] px-1 py-2.5 text-[13px] dark:border-white/[0.06] md:grid-cols-[minmax(0,1.6fr)_repeat(4,minmax(0,.7fr))_minmax(0,1.3fr)] ${TEXT}`}
              >
                <div className="col-span-4 md:col-span-1">
                  <b className="block font-semibold">{z.titel}</b>
                  <span className={`text-[11.5px] ${LEISE}`}>
                    {z.kanalTitel}
                    {z.anfragenJe100Aufrufe !== null && ` · ${z.anfragenJe100Aufrufe.toLocaleString("de-DE")} Anfragen je 100 Aufrufe`}
                  </span>
                </div>
                {(
                  [
                    ["Aufrufe", z.aufrufe > 0 ? de(z.aufrufe) : "—", "eingang-aufrufe"],
                    ["Anfragen", de(z.anzahl), undefined],
                    ["Angebot", de(z.angebote), undefined],
                    ["Gebucht", `${z.gebucht}${z.anzahl ? ` (${Math.round((z.gebucht / z.anzahl) * 100)} %)` : ""}`, undefined],
                  ] as const
                ).map(([lb, wert, id]) => (
                  <div key={lb} className="tabular-nums md:text-right" data-testid={id}>
                    <span className={`block text-[10px] uppercase tracking-[0.06em] md:hidden ${LEISE}`}>{lb}</span>
                    {wert}
                  </div>
                ))}
                <div className="col-span-4 flex flex-col gap-[3px] md:col-span-1" aria-hidden="true">
                  {[z.anzahl, z.angebote, z.gebucht].map((n, i) => (
                    <div
                      key={i}
                      className={`h-1.5 rounded-r ${[BG_S1, BG_S2, BG_S3][i]}`}
                      style={{ width: `${n > 0 ? Math.max((n / maxEingang) * 100, 1) : 0}%` }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <span data-testid="nebenher-checkout">
              <Pille ton="neu" icon="shopping_cart">
                {a.eingaenge.checkoutBestellungen} Checkout-Bestellungen
              </Pille>
            </span>
            <span data-testid="nebenher-vormerkungen">
              <Pille ton="neu" icon="bookmark">
                {a.eingaenge.vormerkungen} Vormerkungen bestätigt
              </Pille>
            </span>
            <span className={`text-[12px] ${LEISE}`}>Diese zählen nicht als Anfragen, ihr Umsatz aber beim Umsatz.</span>
          </div>
        </Abschnitt>
      </div>

      {/* ── Umsatz + Konversion ───────────────────────────────────────────────────────────── */}
      <div className="mt-[22px] grid grid-cols-1 gap-[22px] lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <Abschnitt
          icon="euro"
          titel="Umsatz & Buchungen"
          testId="abschnitt-umsatz"
          rechts={
            <Umschalter
              label="Umsatz zählen nach"
              werte={
                [
                  { wert: "annahme", text: "nach Annahmetag" },
                  { wert: "veranstaltung", text: "nach Veranstaltungsdatum" },
                ] as const
              }
              aktiv={umsatzNach}
              onWahl={(w) => setze({ umsatzNach: w === "annahme" ? null : w })}
            />
          }
        >
          <Legende eintraege={[{ farbe: BG_S1, text: "Zugesagt" }, { farbe: BG_S3, text: "Auf dem Konto" }]} />
          <UmsatzDiagramm a={a} />
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            {oz.anzahl > 0 && (
              <Pille ton="warn" icon="schedule">
                {oz.anzahl} {oz.anzahl === 1 ? "Zahlung" : "Zahlungen"} offen · {ganzeEuro(oz.summeCents)}
              </Pille>
            )}
            {oz.ueberfaellig > 0 && (
              <Pille ton="dang" icon="warning">
                {oz.ueberfaellig} überfällig
                {oz.aeltesteTage !== null ? `, älteste seit ${oz.aeltesteTage} ${oz.aeltesteTage === 1 ? "Tag" : "Tagen"}` : ""}
              </Pille>
            )}
            <Link to="/zahlungen" className="text-[12px] text-primary dark:text-[#f0b49b]">
              Zu den Zahlungen →
            </Link>
          </div>
        </Abschnitt>

        <Abschnitt icon="conversion_path" titel="Angebote & Konversion" testId="abschnitt-konversion">
          <Stufe titel="Anfragen" wert={de(k.anfragen)} anteil={k.anfragen ? 1 : 0} farbe={BG_S1} />
          <Stufe
            titel="Angebot verschickt"
            wert={de(k.angebote)}
            zusatz={k.anfragen ? prozent(k.angebote / k.anfragen) : undefined}
            anteil={k.anfragen ? k.angebote / k.anfragen : 0}
            farbe={BG_S2}
          />
          <Stufe
            titel="Gebucht"
            wert={de(k.auftraege)}
            zusatz={k.buchungsquote ? prozent(k.buchungsquote.anteil) : undefined}
            anteil={k.anfragen ? k.auftraege / k.anfragen : 0}
            farbe={BG_S3}
          />
          <div className="mt-3.5 flex h-3.5 gap-0.5 overflow-hidden rounded" aria-label="Ausgang aller Anfragen" role="img">
            {(
              [
                [k.auftraege, BG_S3, "Gebucht"],
                [k.storniert, BG_WARN, "Storniert"],
                [k.offen, BG_GHOST, "Noch offen"],
                [k.verloren, BG_S2, "Verloren"],
              ] as const
            ).map(([n, farbe, titel]) =>
              n > 0 ? <span key={titel} title={`${titel}: ${n} von ${k.anfragen}`} className={`h-full ${farbe}`} style={{ width: `${(n / k.anfragen) * 100}%` }} /> : null,
            )}
          </div>
          <div className="mt-2">
            <Legende
              eintraege={[
                { farbe: BG_S3, text: `Gebucht ${k.auftraege}` },
                { farbe: BG_WARN, text: `Storniert ${k.storniert}` },
                { farbe: BG_GHOST, text: `Noch offen ${k.offen}` },
                { farbe: BG_S2, text: `Verloren ${k.verloren}` },
              ]}
            />
          </div>
          <p className={`mt-1.5 text-[12px] ${LEISE}`} data-testid="konversion-satz">
            {k.storniert > 0 && (
              <>
                <b className={TEXT}>
                  {k.storniert} {k.storniert === 1 ? "Buchung wurde" : "Buchungen wurden"} storniert.
                </b>{" "}
                Sie stehen getrennt und zählen nicht als Auftrag.{" "}
              </>
            )}
            {k.buchungsquote
              ? `Buchungsquote ${prozent1(k.buchungsquote.anteil)}, belastbarer Bereich ${bereich(k.buchungsquote)}.`
              : "Im Zeitraum ist keine Anfrage eingegangen."}
          </p>
        </Abschnitt>
      </div>

      {/* ── Buchungsstrecke (D5) ──────────────────────────────────────────────────────────── */}
      <div className="mt-[22px]">
        <BuchungsstreckeAbschnitt s={a.buchungsstrecke} />
      </div>

      {/* ── Antwortzeiten ─────────────────────────────────────────────────────────────────── */}
      <div className="mt-[22px]">
        <Abschnitt
          icon="timer"
          titel="Antwortzeiten"
          notiz="Eingang bis zur ersten Nachricht oder zum ersten Angebot aus MAESTRO, auch nachts und am Wochenende"
          testId="abschnitt-antwortzeiten"
        >
          {a.antwortklassen.map((z) => (
            <div key={z.klasse} title={`${z.titel}: ${z.anzahl} Anfragen, daraus ${z.auftraege} Aufträge`} data-testid={`antwort-${z.klasse}`}>
              <Stufe
                titel={
                  <>
                    {z.titel}{" "}
                    <span className={LEISE}>
                      · {z.anzahl} {z.anzahl === 1 ? "Anfrage" : "Anfragen"}
                    </span>
                  </>
                }
                wert={z.quote ? `${prozent(z.quote.anteil)} gebucht` : "—"}
                anteil={(z.quote?.anteil ?? 0) / maxQuote}
                farbe={BG_S1}
              />
            </div>
          ))}
          {a.woranEsLiegt && (
            <p className="mt-3 text-[12px] text-[#57503f] dark:text-[#b8ae9e]">
              <b>Woran es liegt:</b> {a.woranEsLiegt}
            </p>
          )}
        </Abschnitt>
      </div>

      {/* ── KI: EIN Text im 5-€-Deckel (Entscheidung 5) ────────────────────────────────────── */}
      <div className="mt-[22px]">
        <LeadAnalyseKarte zeitraum={zeitraum} />
      </div>

      <Card className="mt-[22px] p-0">
        <details data-testid="so-wird-gezaehlt">
          <summary className={`flex cursor-pointer list-none items-center gap-2 px-[18px] py-3.5 font-bold ${TEXT}`}>
            <Icon name="fact_check" className="text-[18px]" />
            So wird gezählt (eine Definition je Kennzahl)
          </summary>
          <dl className="grid grid-cols-1 gap-2 px-[18px] pb-4 text-[13px] md:grid-cols-[180px_1fr]">
            {DEFINITIONEN.map(([dt, dd]) => (
              <div key={dt} className="contents">
                <dt className={`font-bold ${TEXT}`}>{dt}</dt>
                <dd className="m-0 text-[#57503f] dark:text-[#b8ae9e]">{dd}</dd>
              </div>
            ))}
          </dl>
        </details>
      </Card>
    </>
  );
}
